import { useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { Mol, Reaction } from '@moleculeone/types';

import { styled } from '../../../stitches.config';
import { Checkbox } from '../../controls/Checkbox/Checkbox';
import { SynthesisTree } from '../SynthesisTree/SynthesisTree';
import { Button } from '../../controls/Button/Button';
import { Slider } from '../../controls/Slider/Slider';
import { ButtonCopy } from '../../controls/Button/ButtonCopy';

import { ExpandButton, MoleculeContextProps } from './components/ExpandButton';
import { CommonPointProps, PathButtonGroup } from './components/PathButtonGroup';
import { getReaction } from './utils/getReactionSmiles';
import { pickTreeFromHypertree } from './utils/pickTreeFromHypertree';
import { References } from './components/References';

const MOL_CONTAINER_PADDING = 90;

export interface HypertreeData {
  target: string;
  nodes: Record<
    string,
    {
      type: 'cmpd' | 'rxn';
      id: string;
      pred: string | null;
      succ: string[];
      score: number | null;
      compound_to_map: number[];
    }
  >;
  cmpds: Record<
    string,
    {
      smiles: string;
      quotes: {
        dataset_id: string;
        price: number;
        amount: number;
        availability: number;
      }[];
    }
  >;
  rxns: Record<
    string,
    {
      substrates_to_map: number[][];
      product_to_map: number[];
      prediction: number | null;
    }
  >;
}

export interface Node {
  id: string;
  type: 'compound' | 'reaction';
  predecessor: string | null;
  successors: string[] | null;
  smiles?: string;
  productToMap?: number[];
  substratesToMap?: number[][];
}

const Container = styled('div', {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  paddingBottom: '3rem',
});

const ZoomControls = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
});

const Nav = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '$4',
});

const TreeOverflowContainer = styled('div', {
  overflow: 'auto',
  width: '100%',
});

const TreeContainer = styled('div', {
  paddingTop: '2rem',
  paddingRight: '5rem',
});

const ErrorMessage = styled('div', {
  color: '$red11',
  fontSize: '$2',
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '$4',
});

interface HypertreeProps {
  hypertree: HypertreeData;
  selectedSyntheses?: Array<Record<string, Node>>;
  productToReactionMap: Record<string, number>;
  expandedNodes: string[];
  onChangeSelection?: (selectedSyntheses: Array<Record<string, Node>>) => void;
  onChangeProductToReactionMap: (productToReactionMap: Record<string, number>) => void;
  onChangeExpandedNodes: (expandedNodes: string[]) => void;
  smilesToMol?: (smiles: string) => Mol;
  scifinderToken?: string;
  rrConfig?: Record<string, string>;
}

export function Hypertree(props: HypertreeProps) {
  const {
    hypertree,
    smilesToMol,
    selectedSyntheses = [],
    productToReactionMap,
    expandedNodes,
    onChangeSelection,
    onChangeProductToReactionMap,
    onChangeExpandedNodes,
    scifinderToken,
    rrConfig,
  } = props;

  const [zoom, setZoom] = useState(1);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [lastUpdatedReactionSelection, setLastUpdatedReactionSelection] = useState('');

  const nodes = useMemo(() => {
    try {
      return pickTreeFromHypertree(hypertree, productToReactionMap, expandedNodes);
    } catch (error) {
      console.log(error);
      setParsingError(error instanceof Error ? error.message : (error as string));
      return {};
    }
  }, [hypertree, productToReactionMap, expandedNodes]);

  const isTreeRenderable = Object.keys(nodes).length > 0;
  const isSynthesisSelected = isTreeRenderable && selectedSyntheses.some(synthesis => isEqual(synthesis, nodes));

  const toggleActive = () => {
    const nextSelection = isSynthesisSelected
      ? selectedSyntheses.filter(synthesis => !isEqual(synthesis, nodes))
      : [...selectedSyntheses, nodes];
    onChangeSelection?.(nextSelection);
  };

  const updateProductToReactionMap = (nextProductToReactionMap: Record<string, number>) => {
    onChangeProductToReactionMap(nextProductToReactionMap);
  };

  const updateExpandedNodes = (nextExpandedNodes: string[]) => {
    onChangeExpandedNodes(nextExpandedNodes);
  };

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 't' || e.key === ' ') {
        toggleActive();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleActive]);

  if (parsingError) {
    return <ErrorMessage>Failed to display hypertree: {parsingError}</ErrorMessage>;
  }

  return (
    <Container>
      <ZoomControls>
        Zoom: <i>{zoom.toFixed(1)}</i>
        <Slider
          value={[zoom]}
          onValueChange={value => {
            setZoom(value[0]);
          }}
          min={1}
          max={3}
          step={0.1}
        >
          <Slider.Track>
            <Slider.Range />
          </Slider.Track>
          <Slider.Thumb aria-label="Volume" />
        </Slider>
      </ZoomControls>

      <TreeOverflowContainer>
        <TreeContainer css={{ width: zoom > 1 ? `${zoom * 100}%` : 'auto' }}>
          {!isTreeRenderable && 'No synthesis data loaded'}

          {isTreeRenderable && (
            <SynthesisTree
              tree={nodes}
              smilesToMol={smilesToMol}
              debugEnabled={false}
              skipCache={true}
              renderPointContext={{
                common: (props: CommonPointProps) => {
                  const { productId, reactionData, point, scale } = props;

                  const productNode = hypertree.nodes[productId];
                  const reactionCount = productNode.succ.length;
                  const currentReactionIdx = productToReactionMap[productId] ?? 0;

                  let reactionSmiles = '';
                  let reaction: Reaction | undefined;
                  try {
                    reaction = getReaction(productId, currentReactionIdx, hypertree);
                    reactionSmiles = `${reaction.substrates.join('.')}>>${reaction.product}`;
                  } catch (error) {
                    console.error('Failed to get reaction', productId, error);
                  }

                  const hasMultipleReactions = reactionCount > 1;

                  const xModifier = reactionData.substrateSmiles.length > 1 ? 0 : -20;
                  const commonPointProps = {
                    x: point[0] + xModifier,
                    y: point[1],
                    width: 60,
                    height: hasMultipleReactions ? 62 + (35 + 5) * 2 : 35,
                  };

                  return (
                    <foreignObject
                      x={commonPointProps.x - commonPointProps.width / 2}
                      y={commonPointProps.y - commonPointProps.height / 2}
                      width={commonPointProps.width}
                      height={commonPointProps.height}
                      style={{
                        transform: `scale(${scale}, ${scale})`,
                        transformOrigin: `${commonPointProps.x}px ${commonPointProps.y}px`,
                        overflow: 'visible',
                        textAlign: 'center',
                      }}
                    >
                      {reaction && (
                        <References
                          reactionSmiles={reactionSmiles}
                          reaction={reaction}
                          scifinderToken={scifinderToken}
                          smilesToMol={smilesToMol}
                          rrConfig={rrConfig}
                        />
                      )}
                      {reactionSmiles && (
                        <ButtonCopy
                          copy={reactionSmiles}
                          size="small"
                          weight="minimal"
                          icon="copy"
                          css={{
                            'backgroundColor': 'white',
                            'marginBottom': hasMultipleReactions ? 5 : 0,
                            '& > span': { fontSize: '16px !important' },
                          }}
                        />
                      )}
                      <PathButtonGroup
                        {...props}
                        reactionCount={reactionCount}
                        justUpdated={lastUpdatedReactionSelection === productId}
                        value={currentReactionIdx}
                        onChange={(productId, nextValue) => {
                          const nextProductToReactionMap = {
                            ...productToReactionMap,
                            [productId]: nextValue,
                          };
                          updateProductToReactionMap(nextProductToReactionMap);
                          updateExpandedNodes([...expandedNodes, productId]);
                          setLastUpdatedReactionSelection(productId);
                        }}
                      />
                    </foreignObject>
                  );
                },
              }}
              renderMoleculeContext={(props: MoleculeContextProps) => {
                const { moleculeId } = props;

                if (!props.bbox.size) {
                  return null;
                }

                const node = hypertree.nodes[moleculeId];
                const smiles = hypertree.cmpds[node.id]?.smiles;

                const reactionIdx = productToReactionMap[moleculeId] ?? 0;
                const reactionId = node.succ[reactionIdx];
                const reactionScore = hypertree.nodes[reactionId]?.score ?? Infinity;

                const collapseSuccessors = reactionScore > (node.score ?? Infinity);

                const showExpandButton = reactionId && collapseSuccessors && !expandedNodes.includes(moleculeId);

                return (
                  <>
                    {showExpandButton && (
                      <ExpandButton
                        {...props}
                        onClick={() => {
                          updateExpandedNodes([...expandedNodes, moleculeId]);
                        }}
                      />
                    )}
                    <foreignObject
                      x={props.bbox.min[0] - MOL_CONTAINER_PADDING / props.scale}
                      y={props.bbox.min[1] - (2 * MOL_CONTAINER_PADDING) / props.scale}
                      width={(props.bbox.size[0] + MOL_CONTAINER_PADDING * 2) / props.scale}
                      height={(props.bbox.size[1] + MOL_CONTAINER_PADDING * 2) / props.scale}
                      style={{
                        transform: `scale(${props.scale}, ${props.scale})`,
                        transformOrigin: `${props.bbox.min[0]}px ${props.bbox.min[1]}px`,
                        overflow: 'visible',
                      }}
                    >
                      <ButtonCopy
                        copy={smiles}
                        size="small"
                        weight="minimal"
                        icon="copy"
                        css={{
                          'position': 'absolute',
                          'top': 0,
                          'left': '50%',
                          'transform': 'translateX(-50%)',
                          'backgroundColor': 'white',
                          '& > span': { fontSize: '16px !important' },
                        }}
                      />
                    </foreignObject>
                  </>
                );
              }}
            />
          )}
        </TreeContainer>
      </TreeOverflowContainer>

      <Nav>
        {onChangeSelection && (
          <Checkbox
            id="selected-synthesis"
            label={isSynthesisSelected ? 'Selected synthesis' : 'Select synthesis'}
            isChecked={isSynthesisSelected}
            onCheckedChange={toggleActive}
          />
        )}

        <Button
          icon="restart_alt"
          text="Reset graph"
          onClick={() => {
            updateProductToReactionMap({});
            updateExpandedNodes([]);
          }}
          weight="minimal"
          size="small"
        />
      </Nav>
    </Container>
  );
}
